/**
 * AI 비서 답변의 구조화 마커 파싱.
 *
 * 마커 종류:
 *   <CARDS>{JSON}</CARDS>         — 시각 카드 임베드 (4 type)
 *   <FOLLOWUP>...</FOLLOWUP>      — 추천 후속 질문 칩
 *
 * 2026-05-12 도입 — 텍스트 압축 + 시각화 UX.
 */

const CARDS_RE = /<CARDS>([\s\S]*?)<\/CARDS>/i;
const FOLLOWUP_RE = /<FOLLOWUP>([\s\S]*?)<\/FOLLOWUP>/i;

export interface TrendCardItem {
  id: number;
  keyword: string;
  stage: "mature" | "growing" | "emerging" | "declining";
  score: number;
  mention_30d?: number;
  mention_7d?: number;
}

// 2026-05-18 — 공급처 단위 → 제품 단위 reshape (사용자 결정).
// id = Product.id, 클릭 = product_url 새 탭. manufacturer_company 는 보조 노출.
// 옛 chat history 호환: company/categories/region/summary 는 optional, name 부재 시 legacy 렌더.
export interface SupplierCardItem {
  id: number;                       // Product.id (legacy 에선 Manufacturer.id)
  name?: string;                    // 제품명 (신규 schema 의 식별자)
  spec?: string | null;
  category?: string | null;
  product_url?: string | null;
  manufacturer_company?: string;    // 공급처 이름 (신규)
  premium?: boolean;
  // legacy (~2026-05-17 chat history 호환)
  company?: string;
  categories?: string[];
  region?: string;
  summary?: string;
}

export interface RecipeCardItem {
  id: number;
  title: string;
  channel?: string;
  view_count?: number;
  thumbnail_url?: string;
}

export interface StoreKpiCardItem {
  store_name: string;
  this_week_revenue: number;
  change_pct?: number | null;
  top_menu?: string;
}

export type CardsBlock =
  | { type: "trend"; items: TrendCardItem[] }
  | { type: "supplier"; items: SupplierCardItem[] }
  | { type: "recipe"; items: RecipeCardItem[] }
  | { type: "store_kpi"; items: StoreKpiCardItem[] };

export interface ParsedMessage {
  cleaned: string;         // 마커 제거된 본문
  cards: CardsBlock | null;
  followups: string[];
}

export function parseMessage(text: string): ParsedMessage {
  let cleaned = text;
  let cards: CardsBlock | null = null;
  let followups: string[] = [];

  // CARDS 파싱
  const cardsMatch = cleaned.match(CARDS_RE);
  if (cardsMatch) {
    try {
      const parsed = JSON.parse(cardsMatch[1].trim());
      if (parsed && typeof parsed === "object" && parsed.type && Array.isArray(parsed.items)) {
        cards = parsed as CardsBlock;
      }
    } catch {
      // JSON 깨짐 시 무시
    }
    cleaned = cleaned.replace(CARDS_RE, "");
  }

  // FOLLOWUP 파싱
  const followupMatch = cleaned.match(FOLLOWUP_RE);
  if (followupMatch) {
    followups = followupMatch[1]
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .slice(0, 3);
    cleaned = cleaned.replace(FOLLOWUP_RE, "");
  }

  return { cleaned: cleaned.trimEnd(), cards, followups };
}
